import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ShieldCheck, CheckCircle2, AlertTriangle, XCircle, Clock, ArrowUpRight } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

function TreeBar({ tree }) {
  const score = tree.overall_score ?? 0;
  const color = score >= 80 ? "bg-bm-green" : score >= 65 ? "bg-bm-amber" : "bg-bm-danger";
  const label = score >= 80 ? "PASS" : score >= 65 ? "WATCH" : "FAIL";
  const Icon = score >= 80 ? CheckCircle2 : score >= 65 ? AlertTriangle : XCircle;
  return (
    <div className="flex items-center gap-2">
      <Icon className={`w-3 h-3 ${score >= 80 ? "text-bm-green" : score >= 65 ? "text-bm-amber" : "text-bm-danger"} shrink-0`} />
      <span className="w-24 label-mono text-bm-dim shrink-0">{(tree.tree_type || "").toUpperCase()}</span>
      <div className="flex-1 h-1.5 bg-white/5 relative">
        <div className={`h-full ${color}`} style={{ width: `${score}%` }}></div>
      </div>
      <span className={`w-12 text-right font-mono text-xs ${score >= 80 ? "text-bm-green" : score >= 65 ? "text-bm-amber" : "text-bm-danger"}`}>
        {score}
      </span>
      <span className="w-12 text-right label-mono text-bm-dim">{label}</span>
    </div>
  );
}

function ValidationRow({ opp, idx }) {
  const trees = opp.validation_trees || [];
  const passCount = trees.filter((t) => (t.overall_score ?? 0) >= 80).length;
  const watchCount = trees.filter((t) => (t.overall_score ?? 0) >= 65 && (t.overall_score ?? 0) < 80).length;
  const failCount = trees.filter((t) => (t.overall_score ?? 0) < 65).length;
  return (
    <Link
      to={`/opportunities/${opp.id}`}
      data-testid={`validation-row-${opp.id}`}
      className="bm-card fade-up p-4 flex flex-col gap-3 group"
      style={{ animationDelay: `${Math.min(idx * 30, 400)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="label-mono text-bm-cyan">{opp.category}</span>
            {opp.subcategory && <span className="label-mono text-bm-amber">· {opp.subcategory}</span>}
            {opp.signal_type && <span className="label-mono text-bm-dim">· {opp.signal_type}</span>}
          </div>
          <h3 className="font-heading text-base font-bold leading-tight text-white truncate group-hover:text-bm-amber transition-colors">
            {opp.product_nomenclature || opp.product_name}
          </h3>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-center">
            <div className={`font-heading text-2xl font-black leading-none ${opp.scores?.overall >= 80 ? "text-bm-green" : opp.scores?.overall >= 65 ? "text-bm-amber" : "text-bm-danger"}`}>
              {opp.scores?.overall ?? "—"}
            </div>
            <div className="label-mono text-bm-dim mt-0.5">CONV</div>
          </div>
          <ArrowUpRight className="w-4 h-4 text-bm-dim group-hover:text-bm-amber" />
        </div>
      </div>

      <div className="flex items-center gap-3 text-[10px] font-mono pt-2 border-t border-white/5">
        <span className="text-bm-green">{passCount} PASS</span>
        <span className="text-bm-amber">{watchCount} WATCH</span>
        <span className="text-bm-danger">{failCount} FAIL</span>
        <span className="text-bm-dim ml-auto flex items-center gap-1">
          <Clock className="w-3 h-3" />{formatGeneratedAt(opp.generated_at || opp.created_at)}
        </span>
      </div>

      <div className="space-y-1.5">
        {trees.slice(0, 5).map((t) => (
          <TreeBar key={`${opp.id}-${t.tree_type}`} tree={t} />
        ))}
      </div>
    </Link>
  );
}

export default function Validation() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    api.opportunities({ limit: 100 }).then((d) => {
      setItems(d.items || []);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    if (!statusFilter) return items;
    return items.filter((o) => o.status === statusFilter);
  }, [items, statusFilter]);

  const statuses = ["draft", "validated", "in_pipeline", "launched"];

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-bm-amber flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />MULTI-LAYER VALIDATION
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            What's been put through the trees.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-3xl">
            Every opportunity goes through 5 validation trees — Category · Behaviour · Product · Localization · Commercial.
            This view shows how each opportunity scores across all five, sorted by overall conviction.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="validation-status-filters">
          <button
            onClick={() => setStatusFilter("")}
            data-testid="validation-filter-all"
            className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
              !statusFilter ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
            }`}
          >
            ALL
          </button>
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`validation-filter-${s}`}
              className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
                statusFilter === s ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
              }`}
            >
              {s.replaceAll("_", " ").toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="font-mono text-xs text-bm-cyan scanline p-6">RUNNING VALIDATION TREES...</div>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 font-mono text-sm text-bm-muted">No opportunities in this state.</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="validation-grid">
        {filtered.map((opp, i) => <ValidationRow key={opp.id} opp={opp} idx={i} />)}
      </div>
    </div>
  );
}
